import React, {
  Component,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useLayoutEffect,
  RefObject,
  ReactNode,
} from 'react';
import { useSelector } from 'react-redux';
import { WidgetData, isSafari } from './utils';
import styled from 'styled-components';
import {
  FOOTER_PLACEHOLDER_ID,
  FOOTER_SECTION_ID,
  HEADER_PLACEHOLDER_ID,
  HEADER_SECTION_ID,
  widgetTypes,
} from '../../../builder/util/constants';
import { useLocation } from 'react-router-dom';
import { Dashboard } from 'builder/components/Dashboard';
import { selectUser } from 'shared/state/misc/oidc';

export interface W
  extends Pick<
    WidgetData,
    'id' | 'x' | 'y' | 'children' | 'fullWidth' | 'height'
  > {
  type: string;
}

interface PreviousBottoms {
  [key: string]: number;
}

const STICKY_BOTTOM_PLACEMENT = 'Sticky Bottom (Fixed to Screen)';

const getSessionStorageValue = (key: string): string | null => {
  try {
    if (typeof sessionStorage === 'undefined') {
      return null;
    }
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const isStickyBottomBanner = (widget: Pick<WidgetData, 'type' | 'config'>) =>
  widget.type === widgetTypes.Banner &&
  widget.config?.placement === STICKY_BOTTOM_PLACEMENT;

/**
 * Determines whether a root-level full-width section is purely decorative and
 * should not reserve vertical space for subsequent siblings.
 *
 * Any empty fullWidth non-header/footer section is purely decorative
 * (background colour/image band) and must never reserve vertical space for
 * siblings.  The y-positions of all content widgets are already encoded in
 * the builder manifest, so adding the section's height to `previousBottoms`
 * only creates layout errors — especially at browser zoom levels where
 * window.innerWidth < 1280 causes the section's computed `newX` to shift
 * from a negative offset (e.g. -80) to 0, making its narrow manifest `width`
 * suddenly overlap with content widgets and push them hundreds of pixels down.
 *
 * @param widget Widget being evaluated during layout.
 * @param isChild Whether the widget belongs to a parent section.
 * @returns True when the section should be excluded from sibling stacking.
 */
const isNonBlockingBackgroundSection = (
  widget: WidgetData,
  isChild: boolean
): boolean =>
  widget.type === widgetTypes.Section &&
  widget.fullWidth === true &&
  !isChild &&
  widget.children.length === 0 &&
  widget.id !== HEADER_SECTION_ID &&
  widget.id !== FOOTER_SECTION_ID;

/**
 * Returns the current rendered height for a dynamic widget.
 *
 * MyApps cards can overflow their fixed PageLayout wrapper when responsive
 * wrapping adds rows. Its scroll height therefore represents the space the
 * footer must reserve, while other dynamic widgets retain their established
 * offset-height behavior.
 *
 * @param widget Widget whose rendered height is being evaluated.
 * @param element PageLayout wrapper for the widget.
 * @returns The positive rendered height, or the configured height as fallback.
 */
const getRenderedDynamicWidgetHeight = (
  widget: WidgetData,
  element: HTMLDivElement | null
): number => {
  if (!widget.dynamic || !element) return widget.height;

  const renderedHeight =
    widget.type === widgetTypes.Application
      ? Math.max(element.offsetHeight, element.scrollHeight)
      : element.offsetHeight;

  return renderedHeight > 0 ? renderedHeight : widget.height;
};

/**
 * Calculates the greatest rendered bottom edge among the widgets that should
 * drive footer placement.
 *
 * Nested widgets normally rely on their parent height to drive footer
 * placement. Responsive Application widgets are the exception: API results can
 * add card rows after the parent was laid out, so use the live rendered bottom
 * as a fallback. This is extracted from `updatePositions` so the iteration does
 * not nest more than four functions deep.
 *
 * @param updatedWidgets Widgets whose current positions are being evaluated.
 * @param footerDescendantIds Ids of widgets that render with the footer section.
 * @param widgetRefs Refs to the rendered widget wrappers, keyed by widget id.
 * @returns The maximum bottom Y coordinate that content occupies.
 */
const computeFooterMaxBottom = (
  updatedWidgets: WidgetData[],
  footerDescendantIds: string[],
  widgetRefs: React.MutableRefObject<{
    [key: string]: RefObject<HTMLDivElement>;
  }>
): number => {
  let maxBottom = 0;
  updatedWidgets.forEach((widget: WidgetData) => {
    const parentId = widget.config?.parentId;
    const isNestedChild =
      parentId &&
      parentId !== 'root' &&
      parentId !== HEADER_SECTION_ID &&
      parentId !== FOOTER_SECTION_ID;
    const isResponsiveApplication =
      widget.type === widgetTypes.Application &&
      widget.dynamic === true &&
      widget.visible !== false;
    if (
      widget.id !== FOOTER_SECTION_ID &&
      !isStickyBottomBanner(widget) &&
      !footerDescendantIds.includes(widget.id) &&
      (!isNestedChild || isResponsiveApplication)
    ) {
      const renderedHeight = isResponsiveApplication
        ? getRenderedDynamicWidgetHeight(
            widget,
            widgetRefs.current[widget.id]?.current
          )
        : widget.height;
      const bottom = widget.y + renderedHeight;
      if (bottom > maxBottom) {
        maxBottom = bottom;
      }
    }
  });

  return maxBottom;
};

/**
 * Re-anchors footer descendants to the footer's final position.
 *
 * Footer descendants render as absolute-positioned siblings of the footer
 * section, not as DOM children inside it. Shift the complete descendant tree by
 * the footer's final movement delta so its links, text, and nested groups
 * remain visually attached to the section. Extracted from `updatePositions` to
 * keep function nesting within four levels.
 *
 * @param footerDescendantIds Ids of the widgets that render with the footer.
 * @param updatedWidgets Widgets whose positions are being mutated in place.
 * @param originalPositions Original layout positions captured on first render.
 * @param footerYDelta The vertical distance the footer moved from its origin.
 * @returns True when at least one descendant position changed.
 */
const shiftFooterDescendants = (
  footerDescendantIds: string[],
  updatedWidgets: WidgetData[],
  originalPositions: { id: string; originalY: number }[],
  footerYDelta: number
): boolean => {
  let changed = false;
  footerDescendantIds.forEach(descendantId => {
    const descendant = updatedWidgets.find(
      candidate => candidate.id === descendantId
    );
    const originalDescendant = originalPositions.find(
      position => position.id === descendantId
    );

    if (!descendant || !originalDescendant) return;

    const descendantY = originalDescendant.originalY + footerYDelta;
    if (descendant.y !== descendantY) {
      descendant.y = descendantY;
      changed = true;
    }
  });

  return changed;
};

/**
 * Compile-time prop contract for the styled `Widget` wrapper.
 *
 * This interface is the TypeScript-idiomatic equivalent of runtime PropTypes: it
 * is enforced by the type-checker on every render, guaranteeing `type` is always a
 * string and that positional/size fields are numbers. Note these props only drive
 * layout/styling — user content is injected as already-escaped React nodes via
 * `content`, so there is no `dangerouslySetInnerHTML` path here to introduce XSS.
 */
interface WidgetProps {
  id: string;
  type: string;
  dynamic?: boolean;
  fullWidth?: boolean;
  isParentFullWidth?: boolean;
  parentId?: string;
  width: number;
  height: number;
  isSafariNav?: boolean;
  x: number;
  y: number;
  ref: React.RefObject<HTMLDivElement>;
  content?: ReactNode | ReactNode[];
  headerChildrenIds: string[];
  footerChildrenIds: string[];
  visible?: boolean;
  site?: any;
  // The sticky banner's own content escapes via position:fixed, so its wrapper
  // must collapse to 0 instead of reserving its configured height in main.
  isStickyBanner?: boolean;
}

const Widget = styled.div<WidgetProps>`
  /* MyApps must be content-sized in the client. A fixed wrapper height leaves
     newly wrapped card rows outside the element's layout box, so parent
     sections and the footer continue to use the pre-resize height. */
  height: ${props =>
    props.isStickyBanner
      ? '0'
      : props.dynamic && props.type === widgetTypes.Application
      ? 'auto'
      : props.height
      ? `${props.height}px`
      : 'auto'};
  ${props =>
    !props.isStickyBanner &&
    props.type !== widgetTypes.RichText &&
    !(props.dynamic && props.type === widgetTypes.Application) &&
    props.visible !== false &&
    `min-height: ${props.height}px;`}
  width: ${props => (props.fullWidth ? '100vw' : `100%`)};
  ${props => {
    if (!props.fullWidth) {
      let widthDivisor = 1280;
      if (
        props.isParentFullWidth ||
        ((props.footerChildrenIds.includes(props.id) ||
          props.headerChildrenIds.includes(props.id)) &&
          window.innerWidth >= 1280)
      ) {
        widthDivisor = window.innerWidth - (window.innerWidth - 1280);
      }
      if (
        props.footerChildrenIds.includes(props.id) ||
        props.headerChildrenIds.includes(props.id)
      ) {
        return `max-width: ${Math.min(
          (props.width / widthDivisor) * 100,
          (props.width / window.innerWidth) * 100
        )}%;`;
      } else {
        return `max-width: ${(props.width / widthDivisor) * 100}%;`;
      }
    }
  }}
  position: absolute;
  // Visibility: an explicitly hidden widget (e.g. an inactive Navigation tab's
  // child) is removed from layout with display:none so it cannot overlay or
  // intercept clicks meant for sibling widgets stacked beneath it.
  display: ${props => (props.visible === false ? 'none' : 'grid')};
  grid-template-columns: 1fr;
  // Overflow: clip the UploadWidget so its embedded Swagger UI scrolls inside its
  // own box; all other widgets stay 'visible' so dropdowns/popovers are not cut off.
  overflow: ${props =>
    props.type === widgetTypes.UploadWidget ? 'hidden' : 'visible'};
  // UploadWidget (e.g. interactive Swagger UI) must stack above an overlapping
  // Navigation/Slideshow widget. Both rules use !important so the comparison is a
  // deterministic numeric one (4 > 3) regardless of declaration order or specificity.
  z-index: ${props =>
    props.type === widgetTypes.UploadWidget ? '4 !important' : 'auto'};
  ${props => {
    // Navigation and Slideshow render their own overlay UI, so they are raised above
    // ordinary widgets (z-index: 3) but kept below the UploadWidget rule above.
    if (
      props.type === widgetTypes.Navigation ||
      props.type === widgetTypes.Slideshow
    ) {
      return `z-index: 3 !important;`;
    }
  }}
  ${props => {
    let widthDivisor =
      (props.isParentFullWidth || props.fullWidth) && window.innerWidth >= 1280
        ? window.innerWidth - (window.innerWidth - 1280)
        : 1280;
    if (
      props.footerChildrenIds.includes(props.id) ||
      props.headerChildrenIds.includes(props.id)
    ) {
      widthDivisor = window.innerWidth >= 1280 ? window.innerWidth : 1280;
    }
    return `left: ${(props.x / widthDivisor) * 100}%;`;
  }}
  top: ${props => `${props.y}px`};
`;

const HeaderSectionContainer = styled.div<{
  stickyHeaderEnabled?: boolean;
  isPreviewMode?: boolean;
}>`
  width: 100%;
  position: ${p => (p.stickyHeaderEnabled ? 'sticky' : 'relative')};
  top: ${p => (p.isPreviewMode ? '4rem' : '0rem')};
  z-index: 2;
`;

const FooterSectionContainer = styled.div<{
  stickyFooterEnabled?: boolean;
  footerSectionOffset?: number;
  $footerHeight?: number;
}>`
  width: 100%;
  position: ${p => (p.stickyFooterEnabled ? 'fixed' : 'relative')};
  top: ${p => (p.stickyFooterEnabled ? p.footerSectionOffset : 0)}px;
  z-index: 2;
  /* In children mode the footer widget children are position:absolute and
     collapse the container to 0px. An explicit height restores it. */
  ${p => (p.$footerHeight ? `height: ${p.$footerHeight}px;` : '')}
`;

/**
 * Returns the style object for the PageLayout root div.
 *
 * @param pageBackground - the page background config (may be null)
 * @param children - optional child slot content
 * @param getTotalPageHeight - function returning the total canvas height in px
 * @returns CSSProperties-compatible style object
 */
function getPageLayoutRootStyle({
  pageBackground,
  children,
  getTotalPageHeight,
}: {
  pageBackground: any;
  children: React.ReactNode;
  getTotalPageHeight: () => number;
}): React.CSSProperties {
  return {
    width: '100%',
    background: pageBackground ? pageBackground.color : '#FFFFFF',
    // For widget-canvas pages use the exact calculated pixel height.
    // For child-slot pages (e.g. search results) use min-height + flex so
    // the background stretches from top all the way to the footer bottom.
    height: children ? undefined : `${getTotalPageHeight()}px`,
    minHeight: children ? '100vh' : undefined,
    display: children ? 'flex' : undefined,
    flexDirection: children ? 'column' : undefined,
    position: 'absolute',
    top: 0,
    left: 0,
  };
}

/**
 * Error boundary that catches rendering errors inside PageLayout children
 * and displays a fallback UI instead of an uncaught exception.
 */
class PageLayoutErrorBoundary extends Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('PageLayout rendering error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <div>Something went wrong in the page layout.</div>;
    }
    return this.props.children;
  }
}

interface PageLayoutProps {
  initialWidgets: any;
  currentPage: any;
  // Use children to render a page with a header and footer and content passed in that is not part of site.json
  children?: React.ReactNode;
  site?: Site;
}

const boostWidgetZIndex = (target: HTMLElement): void => {
  target.style.setProperty('z-index', '9999', 'important');
  const headerEl = target.closest('#header-container');
  if (headerEl instanceof HTMLElement) {
    headerEl.style.setProperty('z-index', '9999', 'important');
  }
};

const resetWidgetZIndex = (target: HTMLElement): void => {
  target.style.removeProperty('z-index');
  const headerEl = target.closest('#header-container');
  if (headerEl instanceof HTMLElement) {
    headerEl.style.removeProperty('z-index');
  }
};

const PageLayout: React.FC<PageLayoutProps> = ({
  initialWidgets,
  currentPage,
  children,
  site,
}) => {
  const location = useLocation();
  const isPreviewMode = location.pathname.includes('/preview');

  // Get user token from Redux state for chatbot authentication
  const user = useSelector(selectUser());
  const userToken = user?.access_token;

  const [widgets, setWidgets] = useState(initialWidgets);
  const [isLayoutUpdated, setIsLayoutUpdated] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [footerUpdated, setFooterUpdated] = useState(false);

  useEffect(() => {
    setWidgets(initialWidgets);
  }, [initialWidgets]);

  const originalPositionsRef = useRef(
    widgets.map((widget: WidgetData) => ({
      id: widget.id,
      height: widget.height,
      originalX: widget.x,
      originalY: widget.y,
    }))
  );
  // Store a reference to the original leftBoundingBox on initial page load. This is used for updating x positions of
  // header and footer items as the viewport size changes
  const leftBoundingBoxEdgeRef = useRef<number>(
    window.innerWidth >= 1280 ? (window.innerWidth - 1280) / 2 : 0
  );

  const parentElementRef = useRef(null);

  const widgetRefs = useRef<{ [key: string]: RefObject<HTMLDivElement> }>({});

  useMemo(() => {
    widgetRefs.current = widgets.reduce(
      (acc: { [x: string]: React.RefObject<unknown> }, widget: W) => {
        acc[widget.id] = React.createRef();
        return acc;
      },
      {}
    );
  }, [widgets]);

  const memoizedWidgets = useMemo(() => widgets, [widgets]);

  const rawHeaderSection = widgets.find(
    (w: W) => w.id === HEADER_SECTION_ID || w.id === HEADER_PLACEHOLDER_ID
  );

  const headerChildrenIds = useMemo(() => rawHeaderSection?.children ?? [], [
    rawHeaderSection,
  ]);
  const footerSection = widgets.find(
    (w: W) => w.id === FOOTER_SECTION_ID || w.id === FOOTER_PLACEHOLDER_ID
  );
  const footerChildrenIds = useMemo(() => footerSection?.children ?? [], [
    footerSection,
  ]);

  const findParentId = useCallback(
    (widgetId: string): string | undefined => {
      const configuredParentId = (widgets.find(w => w.id === widgetId) as any)
        ?.config?.parentId;
      if (configuredParentId) {
        return configuredParentId;
      }

      const candidateParentIds = widgets
        .filter(w => w.children?.includes(widgetId))
        .map(w => w.id);

      return (
        candidateParentIds.find(parentId => parentId !== 'root') ??
        candidateParentIds[0]
      );
    },
    [widgets]
  );

  const isDescendantOfSection = useCallback(
    (widgetId: string, sectionId: string): boolean => {
      let currentId = widgetId;

      while (currentId && currentId !== 'root') {
        if (currentId === sectionId) {
          return true;
        }
        currentId = findParentId(currentId);
      }

      return false;
    },
    [findParentId]
  );

  const footerDescendantIds = useMemo(
    () =>
      widgets
        .filter(
          w =>
            w.id !== FOOTER_SECTION_ID &&
            isDescendantOfSection(w.id, FOOTER_SECTION_ID)
        )
        .map(w => w.id),
    [isDescendantOfSection, widgets]
  );

  // Build ordered header list: headerSection first, then its children in declared order,
  // then (edge‑case) any header-tagged or mis-associated children not listed in children[]
  const orderedHeader: W[] = [];
  if (rawHeaderSection) {
    orderedHeader.push(rawHeaderSection);
    headerChildrenIds.forEach(id => {
      const child = widgets.find(w => w.id === id);
      if (child) orderedHeader.push(child);
    });
    // fallback: include any strays that claim parentId=headerSection but were not in children[]
    widgets.forEach(w => {
      if (
        w.id !== HEADER_SECTION_ID &&
        headerChildrenIds.indexOf(w.id) === -1 &&
        (w as any)?.config?.parentId === HEADER_SECTION_ID
      ) {
        orderedHeader.push(w);
      }
    });
  }

  // Build footer list including the full descendant tree so grouped footer widgets
  // stay in the footer container instead of leaking into main content.
  const orderedFooter: W[] = [];
  if (footerSection) {
    orderedFooter.push(footerSection);
    footerDescendantIds.forEach(id => {
      const child = widgets.find(w => w.id === id);
      if (child) orderedFooter.push(child);
    });
  }

  // Main content excludes anything in header/footer collections
  const headerIdSet = new Set(orderedHeader.map(w => w.id));
  const footerIdSet = new Set(orderedFooter.map(w => w.id));
  const mainWidgets: W[] = widgets.filter(
    w => !headerIdSet.has(w.id) && !footerIdSet.has(w.id)
  );

  // Identify sticky-bottom banner (at most one should be active).
  const stickyBanner = useMemo<WidgetData | undefined>(
    () =>
      (mainWidgets as WidgetData[]).find(
        w =>
          w.type === widgetTypes.Banner &&
          w.config?.placement === 'Sticky Bottom (Fixed to Screen)'
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [widgets]
  );

  // IDs of widgets that belong to the sticky banner (rendered in the fixed
  // container rather than as ordinary absolute-positioned page widgets).
  const stickyBannerChildrenIdSet = useMemo(
    () => new Set<string>(stickyBanner?.children ?? []),
    [stickyBanner]
  );
  const stickyBannerIsFullScreen =
    stickyBanner?.config?.enableFullScreen === true;
  const stickyBannerId = stickyBanner?.id;

  // Mirror the dismiss key used by BannerWidget so the children container
  // hides in sync when the user clicks the dismiss button.
  const [stickyBannerDismissed, setStickyBannerDismissed] = useState(() => {
    if (!stickyBanner) return false;
    return (
      getSessionStorageValue(`banner_dismissed_${stickyBanner.id}`) === 'true'
    );
  });

  // Re-read the session value when the active banner changes, otherwise a
  // dismissed banner leaves a different page's banner hidden.
  useEffect(() => {
    setStickyBannerDismissed(
      stickyBannerId
        ? getSessionStorageValue(`banner_dismissed_${stickyBannerId}`) ===
            'true'
        : false
    );
  }, [stickyBannerId]);

  // Keep dismiss state in sync if the sessionStorage value changes externally
  // (e.g. BannerWidget's own dismiss button fires first on the same render).
  useEffect(() => {
    if (!stickyBanner) return;
    const key = `banner_dismissed_${stickyBanner.id}`;
    const isDismissed = getSessionStorageValue(key) === 'true';
    if (isDismissed && !stickyBannerDismissed) {
      setStickyBannerDismissed(true);
    }
  });

  // BannerWidget lives in a sibling render tree, so its dismiss click can't update
  // this component's state directly — listen for its dismiss event instead.
  useEffect(() => {
    if (!stickyBanner) return undefined;
    const handleBannerDismissed = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.widgetId === stickyBanner.id) {
        setStickyBannerDismissed(true);
      }
    };
    window.addEventListener('bannerDismissed', handleBannerDismissed);
    return () =>
      window.removeEventListener('bannerDismissed', handleBannerDismissed);
  }, [stickyBanner]);

  // Final structured grouping used for render
  const sectionWidgets = {
    header: orderedHeader,
    // Exclude sticky-banner children only — the banner itself must stay in main
    // since its own content (position:fixed StickyBannerWrapper) is what renders
    // its background; only its wrapper box is absolute (doesn't affect layout).
    main: mainWidgets.filter(w => !stickyBannerChildrenIdSet.has(w.id)),
    footer: orderedFooter,
  };

  const getTotalPageHeight = () => {
    const footer = memoizedWidgets.find((w: W) => w.id === FOOTER_SECTION_ID);
    if (!footer) return 0;
    const footerBottom = footerDescendantIds.reduce((max, id) => {
      const w = memoizedWidgets.find((d: W) => d.id === id);
      return w ? Math.max(max, (w.y as number) + (w.height as number)) : max;
    }, (footer.y as number) + (footer.height as number));
    const bannerOffset =
      stickyBanner && !stickyBannerDismissed ? stickyBanner.height * 1.5 : 0;
    return footerBottom + bannerOffset;
  };

  /**
   * Updates the positions of widgets on the page layout.
   *
   * This function recalculates and updates the positions of all widgets based on their original positions,
   * ensuring that they do not overlap and are correctly placed within their parent containers. It also
   * handles the specific layout requirements for header and footer sections, as well as full-width widgets.
   *
   * Additionally, this function relies on a minor layout calculation to widget y positions for handling Banner
   * widgets, ensuring that their positions are adjusted correctly relative to other widgets. This happens in
   * src/client/components/PageLayout/utils.tsx processWidgetData function during the initial widget data processing.
   *
   * The function uses a combination of sorting, recursive updates, and position adjustments to maintain
   * the correct layout. It also takes into account dynamic widget heights and adjusts parent widget heights
   * accordingly.
   *
   * @callback updatePositions
   * @returns {void}
   *
   * @warning This function is critical for maintaining the correct layout of the page and interaction of
   * parent/children widgets.
   * Any changes to this code should be made with extreme caution, as it can have significant
   * impacts on the overall layout and positioning of widgets. Ensure thorough testing of all possible
   * layout scenarios is performed after any modifications. This testing should include validating various
   * parent/child widget configurations, dynamic widget heights, full-width widgets, and header/footer sections.
   */
  const updatePositions = useCallback(() => {
    let shouldUpdate = false;
    // previousBottoms is used to keep track of the bottom-most Y positions of widgets that have already been
    // processed. This helps in determining the new Y positions for subsequent widgets to ensure they
    // do not overlap with the previously placed widgets
    const previousBottoms: PreviousBottoms = {};
    let maxContentBottom = 0;
    let footerY = 0;
    let headerY = 0;
    // Identify all parent widgets and their children
    const parentChildMap: { [key: string]: string[] } = memoizedWidgets.reduce(
      (acc: { [x: string]: string[] }, widget: W) => {
        if (widget.children && widget.children.length > 0) {
          acc[widget.id] = widget.children;
        }
        return acc;
      },
      {} as { [key: string]: string[] }
    );

    // Sort widgets by original Y position to ensure correct processing order
    const sortedWidgets = memoizedWidgets.sort((a: { id: W }, b: { id: W }) => {
      const originalA = originalPositionsRef.current.find(
        (pos: { id: W }) => pos.id === a.id
      )!.originalY;
      const originalB = originalPositionsRef.current.find(
        (pos: { id: W }) => pos.id === b.id
      )!.originalY;
      return originalA - originalB;
    });

    // Recursively update positions of children
    const updateChildPositions = (parentId: string, parentY: number): void => {
      // Find the parent widget and its original Y position
      const parentWidget = updatedWidgets.find(
        widget => widget.id === parentId
      )!;
      const parentOriginalY = originalPositionsRef.current.find(
        (pos: W) => pos.id === parentId
      )!.originalY;
      const parentYDelta = parentY - parentOriginalY;

      // Sort the children of the parent widget by their original Y positions
      const sortedChildren = parentChildMap[parentId]
        .map(childId => updatedWidgets.find(w => w.id === childId))
        .filter(Boolean)
        .sort((a, b) => {
          const originalA = originalPositionsRef.current.find(
            (pos: W) => pos.id === a.id
          )!.originalY;
          const originalB = originalPositionsRef.current.find(
            (pos: W) => pos.id === b.id
          )!.originalY;
          return originalA - originalB;
        });

      let maxChildBottom = parentY;
      let lastDynamicBottom = parentY;
      let dynamicChildHeightChanged = false;
      // Update positions of all child widgets
      sortedChildren.forEach(childWidget => {
        const leftBoundingBoxEdge =
          window.innerWidth >= 1280 ? (window.innerWidth - 1280) / 2 : 0;
        const isHeaderChild = headerChildrenIds.includes(childWidget.id);
        const childElement = widgetRefs.current[childWidget.id]?.current;
        const childHeight = getRenderedDynamicWidgetHeight(
          childWidget,
          childElement
        );
        const isDynamic = childWidget.dynamic || false;
        const childOriginal = originalPositionsRef.current.find(
          (pos: W) => pos.id === childWidget.id
        )!;

        // Calculate new Y position for the child widget
        let newChildY = isHeaderChild
          ? childOriginal.originalY
          : childOriginal.originalY + parentYDelta;

        if (newChildY < lastDynamicBottom && !isHeaderChild) {
          newChildY = lastDynamicBottom;
        }

        if (newChildY !== childWidget.y) {
          childWidget.y = newChildY;
          shouldUpdate = true;
        }

        if (isDynamic) {
          if (
            childHeight !== childWidget.height ||
            childOriginal.height === null
          ) {
            dynamicChildHeightChanged = true;
            lastDynamicBottom = Math.max(
              lastDynamicBottom,
              newChildY + childHeight
            );
          }
          if (childWidget.height !== childHeight) {
            childWidget.height = childHeight;
            shouldUpdate = true;
          }
        }

        maxChildBottom = Math.max(maxChildBottom, childWidget.y + childHeight);

        if (childWidget.fullWidth) {
          childWidget.x = -leftBoundingBoxEdge;
          shouldUpdate = true;
        }

        // If the child has children, update their positions recursively
        if (parentChildMap[childWidget.id]) {
          updateChildPositions(childWidget.id, newChildY);
        }
      });

      const newParentHeight = maxChildBottom - parentY;

      if (
        dynamicChildHeightChanged &&
        newParentHeight !== parentWidget.height &&
        parentWidget.id !== HEADER_SECTION_ID &&
        !isStickyBottomBanner(parentWidget)
      ) {
        parentWidget.height = newParentHeight;
        shouldUpdate = true;
      }

      if (!isStickyBottomBanner(parentWidget)) {
        maxContentBottom = Math.max(maxContentBottom, maxChildBottom);
        previousBottoms[
          `${parentWidget.x},${parentWidget.x + parentWidget.width}`
        ] = maxChildBottom;
      }
    };

    // Calculate positions for all non-child widgets to prevent overlap
    const updatedWidgets: WidgetData[] = sortedWidgets.map(
      (widget: WidgetData) => {
        // Calculate the left edge of the bounding box for placing fullwidth widgets, header and footer children
        const leftBoundingBoxEdge =
          window.innerWidth >= 1280 ? (window.innerWidth - 1280) / 2 : 0;
        // Keep the ref in sync with the current viewport so the guard condition
        // in updateHeaderFooterChildrenPositions does not block x-updates after
        // a browser zoom change (e.g. 100% → 125%) shifts window.innerWidth
        // across the 1280-px threshold.
        leftBoundingBoxEdgeRef.current = leftBoundingBoxEdge;
        const element = widgetRefs.current[widget.id]?.current;
        const newHeight = getRenderedDynamicWidgetHeight(widget, element);

        if (newHeight !== widget.height) {
          shouldUpdate = true;
        }

        let newX = widget.fullWidth ? 0 : widget.x;
        let newY = originalPositionsRef.current.find(
          (pos: W) => pos.id === widget.id
        )!.originalY;

        // A widget is a child if it appears in any parent's children array, OR
        // if its config.parentId points to a non-root, non-header/footer widget
        // that exists in the layout. The second check handles data inconsistencies
        // where a widget's children array was not updated to include a nested widget
        // even though the nested widget correctly declares its parent via parentId.
        const configParentId = (widget as any)?.config?.parentId;
        const isChild =
          Object.values(parentChildMap).some(children =>
            children.includes(widget.id)
          ) ||
          (!!configParentId &&
            configParentId !== HEADER_SECTION_ID &&
            configParentId !== FOOTER_SECTION_ID &&
            memoizedWidgets.some((w: W) => w.id === configParentId));
        // if the header widget's height is adjusted, this will correct the positioning
        // for the search results page; 102 is the default height of the header
        if (widget.id === HEADER_SECTION_ID) {
          widget.x = 0;
          setHeaderHeight(widget.height - 102);
        } else if (widget.id === FOOTER_SECTION_ID) {
          widget.x = 0;
        }

        // Update positions of header and footer children
        const updateHeaderFooterChildrenPositions = (
          widget: W,
          section: W,
          sectionY: number
        ) => {
          const sectionChildrenIds = section.children || [];

          if (sectionChildrenIds.includes(widget.id)) {
            const originalY = originalPositionsRef.current.find(
              (pos: W) => pos.id === widget.id
            )!.originalY;
            const sectionOriginalY = originalPositionsRef.current.find(
              (pos: W) => pos.id === section.id
            )!.originalY;
            const sectionOriginalX = originalPositionsRef.current.find(
              (pos: W) => pos.id === widget.id
            )!.originalX;
            newX = leftBoundingBoxEdge + sectionOriginalX - 5;
            newY = sectionY + (originalY - sectionOriginalY);
            if (newY !== widget.y || newX !== widget.x) {
              shouldUpdate = true;
              if (
                widget.x === sectionOriginalX ||
                !(
                  leftBoundingBoxEdge + sectionOriginalX <
                  leftBoundingBoxEdgeRef.current
                )
              ) {
                widget.x = newX;
              }
              widget.y = newY;
            }
            setFooterUpdated(true);
          }
        };

        // Header/Footer children specific positioning logic
        if (rawHeaderSection && footerSection) {
          updateHeaderFooterChildrenPositions(
            widget,
            footerSection,
            footerSection.y
          );
          updateHeaderFooterChildrenPositions(
            widget,
            rawHeaderSection,
            headerY
          );
        }

        if (
          isChild ||
          widget.id === HEADER_SECTION_ID ||
          widget.id === FOOTER_SECTION_ID
        ) {
          return widget;
        }

        // Using x position, determine which widgets need to move down because they are under another widget that moves down
        for (const [xRange, bottom] of Object.entries(previousBottoms)) {
          const [startX, endX] = xRange.split(',').map(Number);
          const widgetEndX = newX + widget.width;
          // Account for the fact that full width widgets are placed at the left edge of the screen and can have a negative x value
          if ((newX < endX && widgetEndX > startX) || widget.fullWidth) {
            if (newY < bottom) {
              newY = bottom;
            }
          }
        }

        if (
          !headerChildrenIds.includes(widget.id) &&
          !footerDescendantIds.includes(widget.id) &&
          widget.fullWidth
        ) {
          // If it's full width and in the main content area, place it at the left edge of the screen and ensure it spans the full width
          newX = -leftBoundingBoxEdge;
          shouldUpdate = true;
        }

        if (newY !== widget.y || newX !== widget.x) {
          shouldUpdate = true;
          widget.y = newY;
          widget.x = newX;
        }

        if (
          !isStickyBottomBanner(widget) &&
          !isNonBlockingBackgroundSection(widget, isChild)
        ) {
          previousBottoms[`${newX},${newX + widget.width}`] = newY + newHeight;
        }
        maxContentBottom = newY + newHeight;

        return {
          ...widget,
          height: newHeight,
        };
      }
    );

    // Propagate dynamic child heights before calculating the footer. Running
    // this after footer placement leaves the footer one layout pass behind when
    // MyApps adds or removes responsive card rows.
    updatedWidgets.forEach(widget => {
      if (parentChildMap[widget.id]) {
        updateChildPositions(widget.id, widget.y);
      }
    });

    // Process header and footer sections but return all other widgets as is
    const headerFooterUpdatedWidgets: WidgetData[] = updatedWidgets.map(
      widget => {
        if (widget.id === HEADER_SECTION_ID) {
          headerY = 0;
          if (headerY !== widget.y) {
            shouldUpdate = true;
            widget.y = headerY;
          }
        } else if (widget.id === FOOTER_SECTION_ID) {
          const originalFooterY = originalPositionsRef.current.find(
            (w: W) => w.id === widget.id
          ).originalY;

          maxContentBottom = computeFooterMaxBottom(
            updatedWidgets,
            footerDescendantIds,
            widgetRefs
          );
          footerY = maxContentBottom >= widget.y ? maxContentBottom : widget.y;
          if (footerY !== widget.y || maxContentBottom < footerY) {
            shouldUpdate = true;
            if (maxContentBottom < footerY && footerY < window.innerHeight) {
              widget.y = window.innerHeight - widget.height;
            } else {
              widget.y = maxContentBottom;
            }
            // Ensure the footer is placed in the original location if content length does not need it to move
            if (widget.y < originalFooterY) {
              widget.y = originalFooterY;
            }
            if (children) {
              const parentElement = parentElementRef.current;
              if (parentElement) {
                const rect = parentElement.getBoundingClientRect();
                const parentY = rect.top + window.scrollY;
                widget.y = parentY - widget.height + rect.height;
              }
            }
          }

          // Footer descendants render as absolute-positioned siblings of the
          // footer section, not as DOM children inside it. Shift the complete
          // descendant tree by the footer's final movement delta so its links,
          // text, and nested groups remain visually attached to the section.
          const footerYDelta = widget.y - originalFooterY;
          if (
            shiftFooterDescendants(
              footerDescendantIds,
              updatedWidgets,
              originalPositionsRef.current,
              footerYDelta
            )
          ) {
            shouldUpdate = true;
          }

          setFooterUpdated(true);
        }
        return widget;
      }
    );

    const finalWidgets: WidgetData[] = headerFooterUpdatedWidgets;

    // Check if any widget positions have changed and update state if necessary
    const hasChanged = finalWidgets.some(newWidget => {
      const prevWidget = memoizedWidgets.find((w: W) => w.id === newWidget.id);
      return (
        newWidget.x !== prevWidget.x ||
        newWidget.y !== prevWidget.y ||
        newWidget.height !== prevWidget.height ||
        newWidget.children !== prevWidget.children ||
        footerUpdated
      );
    });

    if (shouldUpdate && hasChanged) {
      setWidgets(finalWidgets);
      setIsLayoutUpdated(true);
      setFooterUpdated(false);
    }
  }, [
    memoizedWidgets,
    headerChildrenIds,
    footerChildrenIds,
    footerDescendantIds,
    widgetRefs,
    initialWidgets,
  ]);

  useEffect(() => {
    const handleResize = () => {
      updatePositions();
      setIsLayoutUpdated(true);
    };

    window.addEventListener('resize', handleResize);
    updatePositions();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [updatePositions]);

  useEffect(() => {
    const mutationObserver = new MutationObserver(() => {
      requestAnimationFrame(() => updatePositions());
    });

    Object.values(widgetRefs.current).forEach(ref => {
      if (ref.current) {
        mutationObserver.observe(ref.current, {
          childList: true,
          subtree: true,
        });
      }
    });

    return () => {
      mutationObserver.disconnect();
    };
  }, [updatePositions]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => updatePositions());
    });

    Object.values(widgetRefs.current).forEach(ref => {
      if (ref.current) {
        resizeObserver.observe(ref.current);
      }
    });

    return () => {
      resizeObserver.disconnect();
    };
  }, [updatePositions]);

  useLayoutEffect(() => {
    if (isLayoutUpdated) {
      setIsLayoutUpdated(false);
    }
  }, [isLayoutUpdated]);

  const handleNavMouseEnter = (e: React.MouseEvent<HTMLElement>) =>
    boostWidgetZIndex(e.currentTarget);

  const handleNavMouseLeave = (e: React.MouseEvent<HTMLElement>) =>
    resetWidgetZIndex(e.currentTarget);

  const handleSearchFocus = (e: React.FocusEvent<HTMLElement>) =>
    boostWidgetZIndex(e.currentTarget);

  const handleSearchBlur = (e: React.FocusEvent<HTMLElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      resetWidgetZIndex(e.currentTarget);
    }
  };

  const placeElement = (widget: WidgetData) => {
    const { content: _content, ...widgetProps } = widget;
    const fullWidth =
      widget.id === HEADER_SECTION_ID ||
      widget.id === FOOTER_SECTION_ID ||
      widget.fullWidth;
    let isSafariNav = false;
    if (
      isSafari() &&
      widget.type === widgetTypes.Navigation &&
      currentPage.content[widget.id].config.style === 'Horizontal'
    ) {
      isSafariNav = true;
    }
    // Use the initialWidget content in order to maintain accurate widget state without having to perform unnecessary layout calculations

    const initialWidget = initialWidgets.find((w: W) => w.id === widget.id);

    // below is a check when parallel activity by other user has widget deleted then initial widget will come as undefined on Preview mode save when user selects Discard changes.
    if (!initialWidget) {
      return null;
    }
    return (
      <Widget
        {...widgetProps}
        data-test-id={`page-layout-widget-${widget.id}`}
        className={widget.id}
        key={widget.id}
        ref={widgetRefs.current[widget.id]}
        fullWidth={fullWidth}
        footerChildrenIds={footerChildrenIds}
        headerChildrenIds={headerChildrenIds}
        visible={initialWidget.visible}
        isSafariNav={isSafariNav}
        isStickyBanner={isStickyBottomBanner(widget)}
        {...(widget.type === widgetTypes.Navigation &&
          currentPage.content[widget.id]?.config?.style === 'Horizontal' && {
            onMouseEnter: handleNavMouseEnter,
            onMouseLeave: handleNavMouseLeave,
          })}
        {...(widget.type === widgetTypes.Search && {
          onFocus: handleSearchFocus,
          onBlur: handleSearchBlur,
        })}
      >
        {initialWidget.content}
      </Widget>
    );
  };
  const { pageBackground } = currentPage;

  const calculateFooterSectionOffset = () => {
    if (footerSection) {
      const footerSectionOffset =
        footerSection.y - (window.innerHeight - footerSection.height) || 0;
      return -footerSectionOffset;
    }
    return 0;
  };

  // Render header first, followed by main and footer
  return (
    <PageLayoutErrorBoundary>
      <div
        style={getPageLayoutRootStyle({
          pageBackground,
          children,
          getTotalPageHeight,
        })}
      >
        <HeaderSectionContainer
          id="header-container"
          data-test-id="header-container"
          className="header-container"
          stickyHeaderEnabled={site?.header?.stickyHeaderEnabled}
          isPreviewMode={isPreviewMode}
        >
          {sectionWidgets.header.map(placeElement)}
        </HeaderSectionContainer>
        <div
          id="main-container"
          data-test-id="main-container"
          className="main-container"
          style={{
            maxWidth: '1280px',
            width: '100%',
            margin: isPreviewMode ? '4rem auto 0' : '0 auto',
            position: 'relative',
            // Grow to fill available space between header and footer in
            // children mode so the background is contiguous top-to-footer.
            flex: children ? '1 0 auto' : undefined,
          }}
        >
          {!children && sectionWidgets.main.map(placeElement)}
          {/* Sticky-banner children: render inside a fixed-bottom container
              that matches the banner's height so they stay anchored visually. */}
          {stickyBanner &&
            !stickyBannerDismissed &&
            stickyBanner.children?.length > 0 && (
              <div
                data-test-id="sticky-banner-children-container"
                style={{
                  position: 'fixed',
                  bottom: 0,
                  left: stickyBannerIsFullScreen ? 0 : '50%',
                  right: stickyBannerIsFullScreen ? 0 : 'auto',
                  width: stickyBannerIsFullScreen
                    ? 'auto'
                    : 'min(100vw, 1280px)',
                  transform: stickyBannerIsFullScreen
                    ? 'none'
                    : 'translateX(-50%)',
                  height: `${stickyBanner.height}px`,
                  maxHeight: '50vh',
                  overflowY: 'auto',
                  zIndex: 1000,
                  pointerEvents: 'none',
                }}
              >
                <div
                  style={{
                    position: 'relative',
                    maxWidth: '1280px',
                    margin: '0 auto',
                    height: '100%',
                  }}
                >
                  {(stickyBanner.children as string[]).map(childId => {
                    const child = widgets.find(
                      (w: WidgetData) => w.id === childId
                    ) as WidgetData | undefined;
                    const initialChild = initialWidgets.find(
                      (w: W) => w.id === childId
                    );
                    if (!child || !initialChild) return null;
                    return (
                      <div
                        key={childId}
                        ref={widgetRefs.current[childId]}
                        style={{
                          position: 'absolute',
                          top: `${child.y - stickyBanner.y}px`,
                          left: `${(child.x / 1280) * 100}%`,
                          width: `${(child.width / 1280) * 100}%`,
                          height: `${child.height}px`,
                          pointerEvents: 'auto',
                        }}
                      >
                        {initialChild.content}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          {children && (
            <div
              ref={parentElementRef}
              id="page-layout-children"
              style={{ paddingTop: `${headerHeight}px` }}
            >
              {children}
            </div>
          )}
          {site?.header?.enableChatBot && (
            <Dashboard site={site} bearerToken={userToken} />
          )}
        </div>
        <FooterSectionContainer
          id="footer-container"
          data-test-id="footer-container"
          className="footer-container"
          stickyFooterEnabled={site?.footer?.stickyFooterEnabled}
          footerSectionOffset={calculateFooterSectionOffset()}
          $footerHeight={children ? footerSection?.height : undefined}
        >
          {/*
          In children mode (e.g. search results), footer widgets keep their
          page-level y coordinate (for example 1310px) via position:absolute;
          top:Ypx. For non-sticky footers, the container has no matching y
          offset, so content renders off screen below the container unless we
          cancel that y with a negative translateY.

          Sticky footers already apply a fixed-container top offset derived from
          the same footer y coordinate, so applying this translate in sticky
          mode would double-compensate and shift the footer above the viewport.
        */}
          <div
            style={
              children && !site?.footer?.stickyFooterEnabled
                ? { transform: `translateY(-${footerSection?.y ?? 0}px)` }
                : undefined
            }
          >
            {sectionWidgets.footer.map(placeElement)}
          </div>
        </FooterSectionContainer>
      </div>
    </PageLayoutErrorBoundary>
  );
};

export default PageLayout;
